<template>
    <section :class="[this.namespace(), skinStyle]">
        <div :class="[namespace('button-group'), mdsNS('button-group')]">
            <div
                :class="[
                    namespace('button'),
                    mdsNS('button button--secondary'),
                    item.isActive
                        ? `${namespace('tab--active')} ${mdsNS(
                              'button--active'
                          )}`
                        : ''
                ]"
                role="button"
                v-for="item in showItems"
                :key="item.id"
                @click="tabClicked(item, $event)"
            >
                <span :class="namespace('button-text')">{{ item.name }}</span>
                <span
                    :class="[
                        namespace('popover-handler'),
                        namespace('edit-icon')
                    ]"
                    v-if="item.isActive && item.editable"
                >
                    <svg
                        :class="[
                            mdsNS('icon button__icon button__icon--right'),
                            namespace('edit-icon')
                        ]"
                        aria-hidden="true"
                    >
                        <use xlink:href="#caret-down--s"></use>
                    </svg>
                </span>
            </div>
            <div
                v-if="showMoreItems"
                :class="[
                    namespace('button'),
                    namespace('button--more'),
                    mdsNS('button button--secondary')
                ]"
                type="button"
                role="button"
                @click="moreMenuClicked($event)"
            >
                <svg
                    :class="mdsNS('icon button__icon button__icon--left')"
                    aria-hidden="true"
                >
                    <use xlink:href="#ellipsis-horizontal"></use>
                </svg>
            </div>
            <div
                v-if="showAddIcon"
                ref="addIcon"
                :class="[
                    namespace('button'),
                    namespace('button--add'),
                    mdsNS('button button--secondary')
                ]"
                type="button"
                role="button"
                @click="addTab($event)"
            >
                <svg
                    :class="mdsNS('icon button__icon button__icon--left')"
                    aria-hidden="true"
                >
                    <use xlink:href="#plus"></use>
                </svg>
            </div>
        </div>
        <markets-ui-settings
            :class="namespace('popover')"
            :target="popoverTarget"
            :namespaced="namespaced"
            :skin="skin"
            :visible="popoverOptions.visible"
            :title="popoverOptions.title"
            :width="popoverOptions.width"
            @hide="hidePopover"
        >
            <markets-ui-list
                :class="namespace('list')"
                :namespaced="namespaced"
                :skin="skin"
                :dataModel="popoverOptions.list"
                @click="popoverItemClicked"
                @change="popoverItemClicked"
                slot="content"
            >
            </markets-ui-list>
        </markets-ui-settings>
    </section>
</template>
<script>
import utils from '../../utils';
import mixins from '../mixins';
export default {
    name: 'markets-ui-tab-menu',
    mixins: [mixins],
    props: {
        dataModel: {
            type: Array,
            required: true
        },
        showAddIcon: {
            type: Boolean,
            default: true
        },
        editPanel: {
            type: Object,
            default() {
                return {
                    width: 200,
                    title: 'Edit worksheet',
                    list: []
                };
            }
        },
        morePanel: {
            type: Object,
            default() {
                return {
                    width: 200,
                    title: 'View worksheet'
                };
            }
        },
        label: {
            type: Object,
            default() {
                return {
                    rename: 'Rename',
                    delete: 'Delete'
                };
            }
        }
    },
    data() {
        return {
            showMoreItems: false,
            showItems: [],
            popoverTarget: null,
            editPanelVisible: false,
            morePopOverVisible: false
        };
    },
    computed: {
        currentTab() {
            return utils.find(this.dataModel, item => {
                return item.isActive;
            });
        },
        popoverOptions() {
            let options = {};
            if (this.editPanelVisible) {
                options = utils.extend(true, {}, this.editPanel);
                if (!options.list.length) {
                    options.list = [
                        {
                            id: 'rename',
                            click: true,
                            name: this.label['rename']
                        },
                        {
                            id: 'delete',
                            click: true,
                            name: this.label['delete']
                        }
                    ];
                }
            } else if (this.morePopOverVisible) {
                options = utils.extend(true, {}, this.morePanel);
                options.list = this.dataModel.map(item => {
                    return {
                        id: item.id,
                        name: item.name,
                        selected: item.isActive
                    };
                });
            } else {
                options.list = [];
            }
            options.visible = this.editPanelVisible || this.morePopOverVisible;
            return options;
        },
        activeIndex() {
            return utils.findIndex(this.dataModel, item => {
                return item.isActive;
            });
        }
    },
    watch: {
        dataModel: {
            handler(value, oldValue) {
                const textChanged =
                    this._getTabText(value) !== this.previousTabText;
                let showIndex = -1;
                if (!textChanged) {
                    showIndex = utils.findIndex(this.showItems, item => {
                        return item.id === this.dataModel[this.activeIndex].id;
                    });
                }
                if (showIndex > -1) {
                    // The active item is already on the showItems
                    this.showItems.forEach((item, index) => {
                        if (index === showIndex) {
                            item.isActive = true;
                        } else {
                            item.isActive = false;
                        }
                    });
                    this.showItems[showIndex].isActive = true;
                } else {
                    this.showItems = utils.extend(true, [], this.dataModel);
                    this.$nextTick(() => {
                        this.calculateShowItems(textChanged);
                    });
                }
                this.previousTabText = this._getTabText(value);
            },
            deep: true
        }
    },
    created() {
        this.namespace = utils.namespace('ui-tab-menu', false);
        this.showItems = utils.extend(true, [], this.dataModel);
        this.throttleResize = utils.throttle(entry => {
            this.width = entry.contentRect.width;
            this.showItems = utils.extend(true, [], this.dataModel);
            this.morePopOverVisible = false;
            this.editPanelVisible = false;
            this.$nextTick(() => {
                this.calculateShowItems();
            });
        }, 200);
    },
    mounted() {
        this.width = utils.innerSizes(this.$el).width;
        this.addIconWidth = this._getAddIconWidth();
        utils.resizeObserver.observe(this.$el, this._resizeObserverCallback);
        this.previousTabText = this._getTabText(this.dataModel);
    },
    beforeDestroy() {
        utils.resizeObserver.unobserve(this.$el, this._resizeObserverCallback);
    },
    methods: {
        _resizeObserverCallback({ entry }) {
            this.throttleResize(entry);
        },
        tabClicked(tab, e) {
            const target = e.target;
            if (
                (target.classList &&
                    target.classList.contains(this.namespace('edit-icon'))) ||
                /.*SVG(.)*Element/.test(target.toString())
            ) {
                this.showEditPanel(e);
            } else {
                this.editPanelVisible = false;
                if (!tab.isActive) {
                    this.$emit('active-tab-changed', tab.id);
                }
            }
        },
        addTab($event) {
            this.$emit('add-menu-clicked', $event);
        },
        moreMenuClicked(e) {
            this.morePopOverVisible = true;
            this.editPanelVisible = false;
            this.popoverTarget = e.currentTarget;
        },
        showEditPanel(e) {
            this.popoverTarget = e.currentTarget.querySelector(
                `.${this.namespace('popover-handler')}`
            );
            this.editPanelVisible = true;
            this.morePopOverVisible = false;
        },
        hidePopover() {
            this.editPanelVisible = false;
            this.morePopOverVisible = false;
        },
        popoverItemClicked(item) {
            if (this.editPanelVisible) {
                this.$emit('edit-item-clicked', item.id, this.currentTab);
                this.editPanelVisible = false;
            } else if (this.morePopOverVisible) {
                this.$emit('active-tab-changed', item.id);
                this.morePopOverVisible = false;
            }
        },
        calculateShowItems() {
            if (this.activeIndex < 0) {
                return;
            }
            const tabsContainer = this.$el.querySelector(
                `.${this.namespace('button-group')}`
            );
            const tabs = this.$el.querySelectorAll(
                `.${this.namespace('button')}`
            );
            if (tabsContainer) {
                tabsContainer.classList.remove(
                    this.namespace('button-group--more')
                );
            }
            if (tabs.length < this.dataModel.length) {
                return;
            }
            const breakIndex = this._getBreakpointIndex(tabs);
            if (breakIndex !== undefined) {
                this.showMoreItems = true;
                if (breakIndex <= this.activeIndex) {
                    this.showItems = [
                        ...this.dataModel.slice(breakIndex, this.activeIndex),
                        ...this.dataModel.slice(
                            this.activeIndex,
                            this.dataModel.length
                        )
                    ];
                } else if (breakIndex > this.activeIndex) {
                    this.showItems = this.dataModel.slice(
                        this.activeIndex,
                        breakIndex + 1
                    );
                }
                if (tabsContainer) {
                    tabsContainer.classList.add(
                        this.namespace('button-group--more')
                    );
                }
            } else {
                this.showMoreItems = false;
            }
        },
        /**
         * Get the start index of buttons which need to be hidden and shown as '...' when width is not enough
         * @returns{number}
         */
        _getBreakpointIndex(tabs) {
            let tempWidth = 0,
                breakIndex;
            const availableWidth = this.width - this.addIconWidth * 2;
            // the visible priority is ActiveButton=>RightButtons=>LeftButtons
            // try to find the index in the right buttons
            for (let i = this.activeIndex; i < this.dataModel.length; i++) {
                tempWidth += tabs[i].clientWidth;
                if (tempWidth > availableWidth) {
                    breakIndex = i - 1;
                    break;
                }
            }
            // try to find the index in the left buttons
            if (breakIndex === undefined) {
                for (let j = this.activeIndex - 1; j >= 0; j--) {
                    tempWidth += tabs[j].clientWidth;
                    if (tempWidth > availableWidth) {
                        breakIndex = j + 1;
                        break;
                    }
                }
            }
            return breakIndex;
        },
        _getAddIconWidth() {
            let addIconWidth;
            const addIcon = this.$refs.addIcon;
            if (addIcon) {
                addIconWidth = addIcon.clientWidth;
            }
            return addIconWidth;
        },
        _getTabText(tabs) {
            return tabs
                .map(item => {
                    return item.name;
                })
                .join('');
        }
    }
};
</script>
<style lang="scss">
$namespace: 'markets-ui-tab-menu';
@import '../../mds/constants';
.#{$namespace} {
    height: 100%;
    width: 100%;
    &-button-group {
        height: 100%;
        display: flex;
        & .#{$namespace}-button {
            &:first-child {
                border-radius: 0;
            }
            &:last-child {
                border-radius: 0;
            }
        }
    }

    &-button-group--more {
        display: flex;
        justify-content: space-between;
        .#{$namespace}-button {
            flex: auto;
            &--add,
            &--more {
                flex-grow: 0;
                flex-shrink: 0;
            }
        }
    }
    .#{$namespace}-button {
        flex-shrink: 0;
    }
    &-button-text {
        flex-shrink: 0;
        white-space: nowrap;
    }
    &-popover-handler {
        margin-left: 5px;
    }
    &-popover {
        .mds-popover__content,
        .mds-v2-popover__content {
            max-height: 500px;
            overflow-y: auto;
        }
    }
    &-button {
        border-radius: 0;
        box-shadow: none;
        padding: 4px 14px;
        height: 100%;
        align-items: center;
        justify-content: center;
        display: flex;
        &.#{$namespace}-tab--active {
            box-shadow: none !important;
        }
        &:focus {
            box-shadow: none;
        }
        &--add,
        &--more {
            width: 45px;
        }
    }
}
</style>
